import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Button, Card, CardContent, cn } from '@saas-pages/shared/ui'
import { toolError, toolInvoke, toolSuccess } from '@saas-pages/shared/public/runtime'
import { LocalizedProcessingBadge, useT, type Translations } from '@/shared/i18n'
import { ToolHero } from '@/shared/ui/ToolHero'

type ImageToWebpStatus = 'pending' | 'converting' | 'done' | 'error'
type ImageToWebpDirection = 'jpg-to-webp' | 'png-to-webp' | 'mixed-to-webp'
type ImageToWebpErrorCode =
  | 'unsupported_type'
  | 'too_large'
  | 'unsupported_webp'
  | 'corrupt_image'
  | 'canvas_error'
  | 'conversion_failed'

type ImageToWebpCopy = Translations['imageToWebp']

interface ImageToWebpEntry {
  id: string
  file: File
  originalUrl: string | null
  originalSize: number
  convertedBlob: Blob | null
  convertedUrl: string | null
  convertedSize: number | null
  status: ImageToWebpStatus
  error?: string
}

const UTILITY_KEY = 'image-to-webp'
const MAX_FILE_SIZE = 50 * 1024 * 1024
const ACCEPTED_TYPES = ['image/jpeg', 'image/jpg', 'image/png']
const ACCEPTED_EXTENSIONS = ['.jpg', '.jpeg', '.png']

let nextId = 0
let webpEncodeSupport: Promise<boolean> | null = null

function uid(): string {
  return `image-webp-${++nextId}-${Date.now()}`
}

function formatSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
  return `${(bytes / (1024 * 1024)).toFixed(2)} MB`
}

function isSupportedImage(file: File): boolean {
  const type = file.type.toLowerCase()
  const name = file.name.toLowerCase()
  return (
    ACCEPTED_TYPES.includes(type) ||
    ACCEPTED_EXTENSIONS.some((extension) => name.endsWith(extension))
  )
}

function sourceFormat(file: File): 'JPG' | 'PNG' {
  const type = file.type.toLowerCase()
  const name = file.name.toLowerCase()
  return type === 'image/png' || name.endsWith('.png') ? 'PNG' : 'JPG'
}

function displaySourceFormat(entry: ImageToWebpEntry): 'JPG' | 'PNG' | 'FILE' {
  return isSupportedImage(entry.file) ? sourceFormat(entry.file) : 'FILE'
}

function directionForFiles(entries: ImageToWebpEntry[]): ImageToWebpDirection {
  const activeEntries = entries.filter((entry) => entry.status !== 'error')
  const hasPng = activeEntries.some((entry) => sourceFormat(entry.file) === 'PNG')
  const hasJpg = activeEntries.some((entry) => sourceFormat(entry.file) === 'JPG')

  if (hasPng && hasJpg) return 'mixed-to-webp'
  if (hasPng) return 'png-to-webp'
  return 'jpg-to-webp'
}

function validateFile(
  file: File,
  copy: ImageToWebpCopy,
): { message: string; code: ImageToWebpErrorCode } | null {
  if (!isSupportedImage(file)) {
    return { message: copy.errorsText.unsupported, code: 'unsupported_type' }
  }
  if (file.size > MAX_FILE_SIZE) {
    return { message: copy.errorsText.tooLarge, code: 'too_large' }
  }
  return null
}

function outputFilename(inputName: string): string {
  const base = inputName.replace(/\.[^.]+$/, '')
  return `${base || 'converted'}.webp`
}

function uniqueOutputFilename(inputName: string, usedNames: Set<string>): string {
  const filename = outputFilename(inputName)
  if (!usedNames.has(filename)) {
    usedNames.add(filename)
    return filename
  }

  const base = filename.replace(/\.webp$/i, '')
  let counter = 2
  let next = `${base}-${counter}.webp`
  while (usedNames.has(next)) {
    counter += 1
    next = `${base}-${counter}.webp`
  }
  usedNames.add(next)
  return next
}

function downloadBlob(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = filename
  document.body.appendChild(a)
  a.click()
  document.body.removeChild(a)
  URL.revokeObjectURL(url)
}

class ImageToWebpError extends Error {
  constructor(
    message: string,
    readonly code: ImageToWebpErrorCode,
  ) {
    super(message)
  }
}

function loadImage(src: string, copy: ImageToWebpCopy): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image()
    img.onload = () => resolve(img)
    img.onerror = () =>
      reject(new ImageToWebpError(copy.errorsText.corrupt, 'corrupt_image'))
    img.src = src
  })
}

function supportsWebpEncode(): Promise<boolean> {
  if (!webpEncodeSupport) {
    webpEncodeSupport = new Promise((resolve) => {
      const canvas = document.createElement('canvas')
      canvas.width = 1
      canvas.height = 1
      if (!canvas.toBlob) {
        resolve(false)
        return
      }
      canvas.toBlob((blob) => resolve(blob?.type === 'image/webp'), 'image/webp', 0.8)
    })
  }
  return webpEncodeSupport
}

function encodeImageToWebp(
  img: HTMLImageElement,
  quality: number,
  copy: ImageToWebpCopy,
): Promise<Blob> {
  return new Promise((resolve, reject) => {
    const canvas = document.createElement('canvas')
    canvas.width = img.naturalWidth
    canvas.height = img.naturalHeight

    const ctx = canvas.getContext('2d')
    if (!ctx) {
      reject(new ImageToWebpError(copy.errorsText.canvas, 'canvas_error'))
      return
    }

    ctx.drawImage(img, 0, 0)
    canvas.toBlob(
      (blob) => {
        if (blob?.type === 'image/webp') {
          resolve(blob)
          return
        }
        reject(new ImageToWebpError(copy.errorsText.unsupportedWebp, 'unsupported_webp'))
      },
      'image/webp',
      quality / 100,
    )
  })
}

async function convertEntry(
  entry: ImageToWebpEntry,
  quality: number,
  copy: ImageToWebpCopy,
): Promise<Blob> {
  if (!entry.originalUrl) {
    throw new ImageToWebpError(copy.errorsText.corrupt, 'corrupt_image')
  }

  const canEncodeWebp = await supportsWebpEncode()
  if (!canEncodeWebp) {
    throw new ImageToWebpError(copy.errorsText.unsupportedWebp, 'unsupported_webp')
  }

  try {
    const img = await loadImage(entry.originalUrl, copy)
    return await encodeImageToWebp(img, quality, copy)
  } catch (error) {
    if (error instanceof ImageToWebpError) {
      throw error
    }
    throw new ImageToWebpError(copy.errorsText.conversion, 'conversion_failed')
  }
}

function revokeEntry(entry: ImageToWebpEntry): void {
  if (entry.originalUrl) {
    URL.revokeObjectURL(entry.originalUrl)
  }
  if (entry.convertedUrl) {
    URL.revokeObjectURL(entry.convertedUrl)
  }
}

export function ImageToWebpPage() {
  const t = useT()
  const copy = t.imageToWebp
  const [files, setFiles] = useState<ImageToWebpEntry[]>([])
  const [quality, setQuality] = useState(82)
  const [isConverting, setIsConverting] = useState(false)
  const [dropActive, setDropActive] = useState(false)
  const inputRef = useRef<HTMLInputElement>(null)
  const previousQualityRef = useRef(quality)
  const filesRef = useRef<ImageToWebpEntry[]>([])

  useEffect(() => {
    filesRef.current = files
  }, [files])

  useEffect(() => {
    if (previousQualityRef.current === quality) return
    previousQualityRef.current = quality

    setFiles((prev) =>
      prev.map((entry) => {
        if (entry.status === 'pending' || entry.status === 'error') return entry
        if (entry.convertedUrl) {
          URL.revokeObjectURL(entry.convertedUrl)
        }
        return {
          ...entry,
          convertedBlob: null,
          convertedUrl: null,
          convertedSize: null,
          status: 'pending' as const,
          error: undefined,
        }
      }),
    )
  }, [quality])

  useEffect(() => () => {
    filesRef.current.forEach(revokeEntry)
  }, [])

  const addFiles = useCallback(
    (incoming: FileList | File[]) => {
      if (isConverting) return

      const entries = Array.from(incoming).map<ImageToWebpEntry>((file) => {
        const validationError = validateFile(file, copy)
        if (validationError) {
          toolError(UTILITY_KEY, validationError.code, { direction: 'unknown' })
          return {
            id: uid(),
            file,
            originalUrl: null,
            originalSize: file.size,
            convertedBlob: null,
            convertedUrl: null,
            convertedSize: null,
            status: 'error',
            error: validationError.message,
          }
        }

        return {
          id: uid(),
          file,
          originalUrl: URL.createObjectURL(file),
          originalSize: file.size,
          convertedBlob: null,
          convertedUrl: null,
          convertedSize: null,
          status: 'pending',
        }
      })

      setFiles((prev) => [...prev, ...entries])
    },
    [copy, isConverting],
  )

  const handleInputChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      if (event.target.files && event.target.files.length > 0) {
        addFiles(event.target.files)
        event.target.value = ''
      }
    },
    [addFiles],
  )

  const handleDragOver = useCallback((event: React.DragEvent) => {
    event.preventDefault()
    event.stopPropagation()
    setDropActive(true)
  }, [])

  const handleDragLeave = useCallback((event: React.DragEvent) => {
    event.preventDefault()
    event.stopPropagation()
    setDropActive(false)
  }, [])

  const handleDrop = useCallback(
    (event: React.DragEvent) => {
      event.preventDefault()
      event.stopPropagation()
      setDropActive(false)

      if (isConverting) return

      if (event.dataTransfer.files.length > 0) {
        addFiles(event.dataTransfer.files)
      }
    },
    [addFiles, isConverting],
  )

  const removeFile = useCallback((id: string) => {
    if (isConverting) return

    setFiles((prev) => {
      const entry = prev.find((file) => file.id === id)
      if (entry) {
        revokeEntry(entry)
      }
      return prev.filter((file) => file.id !== id)
    })
  }, [isConverting])

  const clearAll = useCallback(() => {
    if (isConverting) return

    files.forEach(revokeEntry)
    setFiles([])
    nextId = 0
  }, [files, isConverting])

  const handleConvert = useCallback(async () => {
    const direction = directionForFiles(files)
    toolInvoke(UTILITY_KEY, { direction })
    setIsConverting(true)

    const startTime = performance.now()
    const pending = files.filter((file) => file.status === 'pending')
    const updated = [...files]
    let runSuccessCount = 0

    for (const entry of pending) {
      const index = updated.findIndex((file) => file.id === entry.id)
      if (index === -1) continue

      updated[index] = { ...entry, status: 'converting', error: undefined }
      setFiles([...updated])

      try {
        const blob = await convertEntry(entry, quality, copy)
        const convertedUrl = URL.createObjectURL(blob)
        updated[index] = {
          ...entry,
          convertedBlob: blob,
          convertedUrl,
          convertedSize: blob.size,
          status: 'done',
        }
        runSuccessCount += 1
        setFiles([...updated])
      } catch (error) {
        const code =
          error instanceof ImageToWebpError ? error.code : 'conversion_failed'
        toolError(UTILITY_KEY, code, { direction })
        updated[index] = {
          ...entry,
          status: 'error',
          error: error instanceof Error ? error.message : copy.errorsText.generic,
        }
        setFiles([...updated])
      }
    }

    const durationMs = Math.round(performance.now() - startTime)
    if (runSuccessCount > 0) {
      toolSuccess(UTILITY_KEY, durationMs, { direction })
    } else {
      toolError(UTILITY_KEY, 'all_failed', { direction })
    }

    setIsConverting(false)
  }, [copy, files, quality])

  const handleDownloadOne = useCallback((entry: ImageToWebpEntry) => {
    if (!entry.convertedBlob) return
    downloadBlob(entry.convertedBlob, outputFilename(entry.file.name))
  }, [])

  const handleDownloadAll = useCallback(async () => {
    const done = files.filter((file) => file.status === 'done' && file.convertedBlob)
    if (done.length === 0) return

    const { default: JSZip } = await import('jszip')
    const zip = new JSZip()
    const usedNames = new Set<string>()

    for (const entry of done) {
      zip.file(uniqueOutputFilename(entry.file.name, usedNames), entry.convertedBlob!)
    }

    const zipBlob = await zip.generateAsync({ type: 'blob' })
    downloadBlob(zipBlob, 'image-to-webp.zip')
  }, [files])

  const stats = useMemo(() => {
    const pending = files.filter((file) => file.status === 'pending').length
    const done = files.filter((file) => file.status === 'done').length
    const errors = files.filter((file) => file.status === 'error').length
    const totalOriginal = files.reduce((sum, file) => sum + file.originalSize, 0)
    const totalConverted = files
      .filter((file) => file.convertedSize !== null)
      .reduce((sum, file) => sum + (file.convertedSize ?? 0), 0)
    return { pending, done, errors, totalOriginal, totalConverted }
  }, [files])

  const canConvert = files.length > 0 && !isConverting && stats.pending > 0

  return (
    <div className="mx-auto max-w-6xl">
      <ToolHero
        eyebrow={copy.eyebrow}
        title={copy.title}
        description={copy.description}
        chips={['JPG, PNG -> WebP', t.common.maxFileSizePerFile]}
        collapsed={files.length > 0}
      />

      <div
        className={cn(
          'grid grid-cols-1 gap-6',
          files.length > 0 && 'lg:grid-cols-[minmax(0,1fr)_340px] lg:items-start',
        )}
      >
        <div className="min-w-0 space-y-6">
          <Card
            className={cn(
              'mx-auto max-w-3xl transition-colors',
              files.length > 0 && 'max-w-none',
              dropActive && 'border-primary bg-primary/5',
            )}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
          >
            <CardContent className="p-4 text-center sm:p-8">
              <div className="mx-auto mb-5 hidden h-16 w-16 place-items-center sm:grid rounded-md border bg-background text-xs font-semibold text-muted-foreground">
                WebP
              </div>
              <p className="mb-2 hidden text-lg font-medium [@media(pointer:fine)]:block">{copy.dropTitle}</p>
              <p
                className={cn(
                  'mx-auto mb-5 max-w-md text-sm leading-6 text-muted-foreground',
                  files.length > 0 && 'hidden sm:block',
                )}
              >
                {copy.dropDescription}
              </p>
              <input
                ref={inputRef}
                type="file"
                accept=".jpg,.jpeg,.png,image/jpeg,image/png"
                multiple
                disabled={isConverting}
                className="hidden"
                onChange={handleInputChange}
              />
              <div className="flex flex-wrap justify-center gap-3">
                <Button
                  disabled={isConverting}
                  onClick={() => inputRef.current?.click()}
                >
                  {copy.chooseFiles}
                </Button>
                {files.length > 0 && (
                  <Button
                    variant="outline"
                    disabled={isConverting}
                    onClick={clearAll}
                  >
                    {t.converter.clearAll}
                  </Button>
                )}
              </div>
            </CardContent>
          </Card>

          {files.length > 0 && (
            <section className="space-y-3">
              <div className="flex flex-wrap items-center justify-between gap-3">
                <h2 className="text-lg font-semibold tracking-normal">
                  {t.converter.filesTitle} ({files.length})
                </h2>
                <div className="flex gap-2">
                  {stats.done > 0 && (
                    <Button variant="outline" size="sm" onClick={handleDownloadAll}>
                      {t.converter.downloadAll} ({stats.done})
                    </Button>
                  )}
                  {canConvert && (
                    <Button size="sm" onClick={handleConvert}>
                      {t.common.convert} ({stats.pending})
                    </Button>
                  )}
                </div>
              </div>

              <div className="grid gap-3 sm:grid-cols-2">
                {files.map((entry) => {
                  const convertedDelta =
                    entry.originalSize > 0 && entry.convertedSize !== null
                      ? Math.round(
                          ((entry.convertedSize - entry.originalSize) / entry.originalSize) *
                            100,
                        )
                      : null

                  return (
                    <Card key={entry.id} className="overflow-hidden shadow-sm">
                      <CardContent className="p-0">
                        <div className="grid grid-cols-2 gap-px bg-border">
                          <div className="bg-muted">
                            <div className="flex items-center justify-between gap-2 px-3 py-2 text-xs font-medium text-muted-foreground">
                              <span>{copy.sourceLabel}</span>
                              <span>{displaySourceFormat(entry)}</span>
                            </div>
                            <div className="aspect-video bg-background/65">
                              {entry.originalUrl ? (
                                <img
                                  src={entry.originalUrl}
                                  alt={entry.file.name}
                                  className="h-full w-full object-contain"
                                />
                              ) : (
                                <div className="grid h-full place-items-center">
                                  <span className="rounded-md border bg-background px-3 py-2 text-xs font-semibold text-muted-foreground">
                                    {displaySourceFormat(entry)}
                                  </span>
                                </div>
                              )}
                            </div>
                          </div>
                          <div className="bg-muted">
                            <div className="flex items-center justify-between gap-2 px-3 py-2 text-xs font-medium text-muted-foreground">
                              <span>{copy.resultLabel}</span>
                              <span>WebP</span>
                            </div>
                            <div className="aspect-video bg-background/65">
                              {entry.convertedUrl ? (
                                <img
                                  src={entry.convertedUrl}
                                  alt={outputFilename(entry.file.name)}
                                  className="h-full w-full object-contain"
                                />
                              ) : (
                                <div className="grid h-full place-items-center">
                                  <span className="rounded-md border bg-background px-3 py-2 text-xs font-semibold text-muted-foreground">
                                    WebP
                                  </span>
                                </div>
                              )}
                            </div>
                          </div>
                        </div>

                        <div className="space-y-4 p-4">
                          <div className="min-w-0">
                            <div
                              className="truncate text-sm font-medium"
                              title={entry.file.name}
                            >
                              {entry.file.name}
                            </div>
                            <div className="mt-2 flex flex-wrap gap-2">
                              <span className="rounded-md border bg-background px-2 py-1 text-xs font-medium text-muted-foreground">
                                {displaySourceFormat(entry)}
                              </span>
                              <span className="rounded-md border bg-background px-2 py-1 text-xs text-muted-foreground">
                                {formatSize(entry.originalSize)}
                              </span>
                              {entry.convertedSize !== null && (
                                <span className="rounded-md border bg-background px-2 py-1 text-xs text-muted-foreground">
                                  {formatSize(entry.convertedSize)}
                                  {convertedDelta !== null && (
                                    <span
                                      className={cn(
                                        'ml-1 font-medium',
                                        convertedDelta <= 0
                                          ? 'text-primary'
                                          : 'text-muted-foreground',
                                      )}
                                    >
                                      {convertedDelta > 0 ? '+' : ''}
                                      {convertedDelta}%
                                    </span>
                                  )}
                                </span>
                              )}
                            </div>
                          </div>

                          {entry.status === 'converting' && (
                            <div className="flex items-center gap-2 text-sm text-muted-foreground">
                              <span className="inline-block h-4 w-4 animate-spin rounded-full border-2 border-current border-t-transparent" />
                              {t.common.converting}
                            </div>
                          )}

                          {entry.status === 'done' && entry.convertedBlob && (
                            <div className="flex flex-wrap items-center justify-between gap-2">
                              <span className="text-sm font-medium text-primary">
                                {t.common.done}
                              </span>
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => handleDownloadOne(entry)}
                              >
                                {t.common.download}
                              </Button>
                            </div>
                          )}

                          {entry.status === 'error' && (
                            <div className="text-sm text-destructive">
                              {entry.error ?? t.common.errorConverting}
                            </div>
                          )}

                          {entry.status === 'pending' && (
                            <Button
                              variant="ghost"
                              size="sm"
                              disabled={isConverting}
                              className="text-destructive hover:text-destructive"
                              onClick={() => removeFile(entry.id)}
                            >
                              {t.common.remove}
                            </Button>
                          )}
                        </div>
                      </CardContent>
                    </Card>
                  )
                })}
              </div>
            </section>
          )}
        </div>

        {files.length > 0 && (
          <aside className="min-w-0 space-y-4 lg:sticky lg:top-24">
            <Card>
              <CardContent className="space-y-5 p-5">
                <div className="flex items-center justify-between gap-3">
                  <h2 className="text-lg font-semibold tracking-normal">
                    {t.converter.settings}
                  </h2>
                  <LocalizedProcessingBadge variant="local" />
                </div>

                <div className="space-y-2">
                  <div className="flex items-center justify-between gap-3">
                    <span className="text-sm font-medium">{copy.qualityLabel}</span>
                    <span className="text-sm tabular-nums text-muted-foreground">
                      {quality}%
                    </span>
                  </div>
                  <input
                    type="range"
                    min={40}
                    max={100}
                    step={1}
                    value={quality}
                    disabled={isConverting}
                    onChange={(event) => setQuality(Number(event.target.value))}
                    className="w-full accent-primary"
                  />
                </div>

                <Button
                  className="w-full"
                  size="lg"
                  onClick={handleConvert}
                  disabled={!canConvert}
                >
                  {isConverting
                    ? t.common.converting
                    : stats.pending > 0
                      ? `${t.common.convert} (${stats.pending})`
                      : t.converter.noFilesQueued}
                </Button>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="space-y-3 p-5">
                <h2 className="text-lg font-semibold tracking-normal">{t.converter.summary}</h2>
                <div className="grid grid-cols-3 gap-2 text-center">
                  <div className="rounded-md bg-muted p-3">
                    <div className="font-semibold tabular-nums">{stats.pending}</div>
                    <div className="text-xs text-muted-foreground">{t.converter.pending}</div>
                  </div>
                  <div className="rounded-md bg-muted p-3">
                    <div className="font-semibold tabular-nums">{stats.done}</div>
                    <div className="text-xs text-muted-foreground">{t.converter.done}</div>
                  </div>
                  <div className="rounded-md bg-muted p-3">
                    <div className="font-semibold tabular-nums">{stats.errors}</div>
                    <div className="text-xs text-muted-foreground">{t.converter.errors}</div>
                  </div>
                </div>

                <div className="space-y-2 text-sm text-muted-foreground">
                  <div className="flex justify-between gap-3">
                    <span>{t.converter.originalSize}</span>
                    <strong className="font-medium text-foreground">
                      {formatSize(stats.totalOriginal)}
                    </strong>
                  </div>
                  {stats.done > 0 && (
                    <div className="flex justify-between gap-3">
                      <span>{t.converter.convertedSize}</span>
                      <strong className="font-medium text-foreground">
                        {formatSize(stats.totalConverted)}
                      </strong>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>

            <p className="rounded-md border bg-card px-3 py-2 text-xs leading-5 text-muted-foreground">
              {copy.privacyNote}
            </p>
          </aside>
        )}
      </div>

      <section className="mt-12 grid gap-4 border-t pt-8 lg:grid-cols-3">
        {copy.faq.map((item) => (
          <div key={item.question}>
            <h2 className="text-base font-semibold tracking-normal">{item.question}</h2>
            <p className="mt-2 text-sm leading-6 text-muted-foreground">
              {item.answer}
            </p>
          </div>
        ))}
      </section>
    </div>
  )
}
